import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Divider,
  Link,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  OpenInNew,
  CalendarToday,
  AccessTime,
} from '@mui/icons-material';
import ArticleTable from '../../components/Blog/ArticleTable';
import SEOHead from '../../components/SEO/SEOHead';
import PageHeader from '../../components/Layout/PageHeader';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import RL31Visual from '../../components/TaxSeason/RL31Visual';
import SolidarityCalendar from '../../components/TaxSeason/SolidarityCalendar';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import { useTranslation } from 'react-i18next';

const SolidaryTaxCreditPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('blog.solidaryTaxCredit.seoTitle'),
    description: t('blog.solidaryTaxCredit.seoDescription'),
    datePublished: '2026-02-21',
    dateModified: '2026-02-21',
    author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    publisher: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com', logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' } },
    mainEntityOfPage: 'https://www.finmaple.com/blog/solidarity-tax-credit-quebec-guia-completa',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.article-headline', '.atomic-answer'],
    },
    inLanguage: i18n.language,
  };

  const PAYMENT_TABLE = [
    { situation: t('blog.solidaryTaxCredit.paymentRow1Situation'), income: t('blog.solidaryTaxCredit.paymentRow1Income'), payment: t('blog.solidaryTaxCredit.paymentRow1Payment') },
    { situation: t('blog.solidaryTaxCredit.paymentRow2Situation'), income: t('blog.solidaryTaxCredit.paymentRow2Income'), payment: t('blog.solidaryTaxCredit.paymentRow2Payment') },
    { situation: t('blog.solidaryTaxCredit.paymentRow3Situation'), income: t('blog.solidaryTaxCredit.paymentRow3Income'), payment: t('blog.solidaryTaxCredit.paymentRow3Payment') },
    { situation: t('blog.solidaryTaxCredit.paymentRow4Situation'), income: t('blog.solidaryTaxCredit.paymentRow4Income'), payment: t('blog.solidaryTaxCredit.paymentRow4Payment') },
  ];

  const COMPARISON_TABLE = [
    { feature: t('blog.solidaryTaxCredit.compRow1Feature'), solidarity: t('blog.solidaryTaxCredit.compRow1Solidarity'), ccb: t('blog.solidaryTaxCredit.compRow1CCB') },
    { feature: t('blog.solidaryTaxCredit.compRow2Feature'), solidarity: t('blog.solidaryTaxCredit.compRow2Solidarity'), ccb: t('blog.solidaryTaxCredit.compRow2CCB') },
    { feature: t('blog.solidaryTaxCredit.compRow3Feature'), solidarity: t('blog.solidaryTaxCredit.compRow3Solidarity'), ccb: t('blog.solidaryTaxCredit.compRow3CCB') },
    { feature: t('blog.solidaryTaxCredit.compRow4Feature'), solidarity: t('blog.solidaryTaxCredit.compRow4Solidarity'), ccb: t('blog.solidaryTaxCredit.compRow4CCB') },
    { feature: t('blog.solidaryTaxCredit.compRow5Feature'), solidarity: t('blog.solidaryTaxCredit.compRow5Solidarity'), ccb: t('blog.solidaryTaxCredit.compRow5CCB') },
  ];

  const FAQS = [
    {
      question: t('blog.solidaryTaxCredit.faq1Q'),
      answer: t('blog.solidaryTaxCredit.faq1A'),
    },
    {
      question: t('blog.solidaryTaxCredit.faq2Q'),
      answer: t('blog.solidaryTaxCredit.faq2A'),
    },
    {
      question: t('blog.solidaryTaxCredit.faq3Q'),
      answer: t('blog.solidaryTaxCredit.faq3A'),
    },
    {
      question: t('blog.solidaryTaxCredit.faq4Q'),
      answer: t('blog.solidaryTaxCredit.faq4A'),
    },
  ];
  const [takeawaysOpen, setTakeawaysOpen] = useState(false);

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={t('blog.solidaryTaxCredit.seoTitle')}
        description={t('blog.solidaryTaxCredit.seoDescription')}
        canonical="/blog/solidarity-tax-credit-quebec-guia-completa"
        routeKey="blogSolidarity"
        seoKey="blogSolidarity"
        noIndex={i18n.language !== 'es'}
        ogImage="https://www.finmaple.com/images/blog/solidarity-tax-credit-quebec.jpg"
        schema={articleSchema}
      />

      <PageHeader
        variant="functional"
        title={t('blog.solidaryTaxCredit.pageTitle')}
        breadcrumbs={[{ label: t('blog.solidaryTaxCredit.breadcrumbGuides'), to: `/${lng}/blog` }, { label: t('blog.solidaryTaxCredit.breadcrumbCurrent') }]}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5F6B7A' }}>
            <CalendarToday sx={{ fontSize: 14 }} />
            <Typography variant="body2">{t('blog.solidaryTaxCredit.updated')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5F6B7A' }}>
            <AccessTime sx={{ fontSize: 14 }} />
            <Typography variant="body2">{t('blog.solidaryTaxCredit.readTime')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#5F6B7A' }}>{t('blog.solidaryTaxCredit.author')}</Typography>
        </Box>
      </PageHeader>

      <TrustStrip />

      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>

        {/* Atomic Answer */}
        <AtomicAnswer text={t('blog.solidaryTaxCredit.atomicAnswer')} />

        {/* Emotional Hook */}
        <Box
          sx={{
            borderLeft: '4px solid #F9A825',
            bgcolor: '#FFFBF0',
            borderRadius: '0 10px 10px 0',
            px: 3,
            py: 2,
            mb: 4,
          }}
        >
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.7, fontWeight: 500 }}>
            <strong>{t('blog.solidaryTaxCredit.hookBold')}</strong> {t('blog.solidaryTaxCredit.hookText')}
          </Typography>
        </Box>

        {/* Key Takeaways */}
        <Box sx={{ border: '1px solid #EEF0F3', borderRadius: '10px', overflow: 'hidden', mt: 5, mb: 5 }}>
          <Box
            onClick={() => setTakeawaysOpen(!takeawaysOpen)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              bgcolor: '#F7F9FC',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#EEEEEE' },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
              {t('blog.solidaryTaxCredit.takeawaysTitle')}
            </Typography>
            {takeawaysOpen ? <ExpandLess sx={{ color: '#5F6B7A' }} /> : <ExpandMore sx={{ color: '#5F6B7A' }} />}
          </Box>
          <Box component="ul" sx={{ display: takeawaysOpen ? 'block' : 'none', px: 3, py: 2, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.6 } }}>
              <li>{t('blog.solidaryTaxCredit.takeaway1')}</li>
              <li>{t('blog.solidaryTaxCredit.takeaway2')}</li>
              <li>{t('blog.solidaryTaxCredit.takeaway3')}</li>
            </Box>
        </Box>

        {/* H2: How much */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.solidaryTaxCredit.section1Title')}
        </Typography>

        <ArticleTable
          columns={[
            { key: 'situation', label: t('blog.solidaryTaxCredit.colSituation') },
            { key: 'income', label: t('blog.solidaryTaxCredit.colIncome') },
            { key: 'payment', label: t('blog.solidaryTaxCredit.colPayment') },
          ]}
          rows={PAYMENT_TABLE.map((row) => ({
            ...row,
            payment: { text: row.payment, sx: { color: '#1E3A5F', fontWeight: 600 } },
          }))}
        />

        {/* H2: Payment calendar */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.solidaryTaxCredit.section2Title')}
        </Typography>

        <SolidarityCalendar />

        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.solidaryTaxCredit.section2P1')}
        </Typography>

        {/* H2: RL-31 */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.solidaryTaxCredit.section3Title')}
        </Typography>

        <RL31Visual />

        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t('blog.solidaryTaxCredit.section3P1Before')}<strong>{t('blog.solidaryTaxCredit.section3P1Bold')}</strong>{t('blog.solidaryTaxCredit.section3P1After')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
          {t('blog.solidaryTaxCredit.section3P2')}
        </Typography>

        {/* H2: Comparison table */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.solidaryTaxCredit.section4Title')}
        </Typography>

        <ArticleTable
          columns={[
            { key: 'feature', label: t('blog.solidaryTaxCredit.colFeature') },
            { key: 'solidarity', label: t('blog.solidaryTaxCredit.colSolidarity') },
            { key: 'ccb', label: t('blog.solidaryTaxCredit.colCCB') },
          ]}
          rows={COMPARISON_TABLE}
        />

        {/* H2: Requirements */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.solidaryTaxCredit.section5Title')}
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
          <li><strong>{t('blog.solidaryTaxCredit.req1Bold')}</strong> {t('blog.solidaryTaxCredit.req1TextBefore')}<RouterLink to={`/${lng}/blog/guia-impuestos-latinos-quebec-2026`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.solidaryTaxCredit.req1Link')}</RouterLink>{t('blog.solidaryTaxCredit.req1TextAfter')}</li>
          <li><strong>{t('blog.solidaryTaxCredit.req2Bold')}</strong> {t('blog.solidaryTaxCredit.req2Text')}</li>
          <li><strong>{t('blog.solidaryTaxCredit.req3Bold')}</strong> {t('blog.solidaryTaxCredit.req3Text')}</li>
          <li><strong>{t('blog.solidaryTaxCredit.req4Bold')}</strong> {t('blog.solidaryTaxCredit.req4Text')}</li>
        </Box>

        {/* H2: Common mistakes */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t('blog.solidaryTaxCredit.section6Title')}
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4, '& li': { mb: 2, color: '#1A1A1A', lineHeight: 1.7 } }}>
          <li><strong>{t('blog.solidaryTaxCredit.mistake1Bold')}</strong> {t('blog.solidaryTaxCredit.mistake1TextBefore')}<RouterLink to={`/${lng}/blog/cra-vs-revenu-quebec-diferencias`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.solidaryTaxCredit.mistake1Link')}</RouterLink>{t('blog.solidaryTaxCredit.mistake1TextAfter')}</li>
          <li><strong>{t('blog.solidaryTaxCredit.mistake2Bold')}</strong> {t('blog.solidaryTaxCredit.mistake2Text')}</li>
          <li><strong>{t('blog.solidaryTaxCredit.mistake3Bold')}</strong> {t('blog.solidaryTaxCredit.mistake3Text')}</li>
          <li><strong>{t('blog.solidaryTaxCredit.mistake4Bold')}</strong> {t('blog.solidaryTaxCredit.mistake4Text')}</li>
          <li><strong>{t('blog.solidaryTaxCredit.mistake5Bold')}</strong> {t('blog.solidaryTaxCredit.mistake5Text')}</li>
        </Box>

        <Divider sx={{ mt: 5, mb: 0 }} />

        {/* FAQ */}
        <FAQSection faqs={FAQS} />

        {/* CTA soft box */}
        <Box
          sx={{
            borderLeft: '4px solid #1E3A5F',
            bgcolor: '#F7F9FC',
            borderRadius: '0 10px 10px 0',
            px: 3,
            py: 2.5,
            my: 4,
          }}
        >
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
            {t('blog.solidaryTaxCredit.ctaText')} <strong>{t('blog.solidaryTaxCredit.ctaBold')}</strong> {t('blog.solidaryTaxCredit.ctaSuffix')}
          </Typography>
        </Box>


        <RelatedArticles slug="solidarity-tax-credit-quebec-guia-completa" />

        {/* Official Sources */}
        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
          <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
            {t('blog.solidaryTaxCredit.officialSources')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Link href="https://www.revenuquebec.ca/en/citizens/tax-credits/solidarity-tax-credit/" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('blog.solidaryTaxCredit.sourceRevenuQuebec')} <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            <Link href="https://www.canada.ca/en/revenue-agency.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('blog.solidaryTaxCredit.sourceCRA')} <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
          </Box>
          <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
            {t('blog.solidaryTaxCredit.footerNote')}
          </Typography>
        </Box>
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default SolidaryTaxCreditPage;
